import type React from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from '../hooks';
import { motion } from 'motion/react';

import {
  selectDidProactiveBubblesShownTrackingEventGetSent,
  selectError,
  selectInitialIntentId,
  selectInitialQuery,
  selectWorkflowsConfig,
} from 'reducers/workflows';
import { selectIsMobile } from 'reducers/main';
import {
  sendTrackingEvent,
  setDidUserCloseProactiveChat,
} from 'actions/workflows/workflows';

import OptionButton from './OptionButton';

import CloseIconDark from 'assets/icons/close-widget-icon-v2.5-dark.svg';
import {
  selectIsPositionLeft,
  selectLaunchStyle,
  selectLegibleButtonOutlineColor,
  selectPlacement,
  selectProactivePromptGreeting,
} from 'reducers/config';
import { postIframeResizeEvent } from 'utils/postIframeResizeEvent';
import safeLinkifyHtml from 'utils/safeLinkifyHtml';
import { useHandleWorkflowTriggerOnClick } from './App/hooks';
import { Tooltip } from '@forethought-technologies/forethought-elements';
import { useHandlePromptIntentOptionClicked } from 'utils/handlePromptIntentOptionClicked';
import {
  DEFAULT_MARGIN,
  FORETHOUGHT_PROACTIVE_CHAT_CONTAINER_ID,
  FORETHOUGHT_PROACTIVE_CHAT_GREETING_MESSAGE_ID,
  FORETHOUGHT_PROACTIVE_CHAT_INTENTS_CONTAINER_ID,
  MESSAGE_PADDING,
} from 'embed/constants';
import sanitizeHtml from 'utils/sanitizeHtml';
import { toggleModalVisibility } from 'actions/workflows-v2/workflowsV2';

interface ProactiveBubblesProps {
  className?: string | undefined;
  /**
   * set to true when this is rendered inside of FORETHOUGHT_HELP_BUTTON_ID
   * to prevent incorrect iframe resizing
   */
  useResizeIds?: boolean;
}

const CloseProactiveBubbles = () => {
  const dispatch = useAppDispatch();
  const legibleButtonOutlineColor = useAppSelector(
    selectLegibleButtonOutlineColor,
  );
  const didProactiveBubblesShownTrackingEventGetSent = useAppSelector(
    selectDidProactiveBubblesShownTrackingEventGetSent,
  );
  const launchStyle = useAppSelector(selectLaunchStyle);

  return (
    <Tooltip
      // Looks like button margin pushes the tooltip away, so bring it
      // closer via `offset`:
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -20],
            },
          },
        ],
      }}
      tooltipContent='Hide floating buttons'
    >
      <motion.div
        animate={useMemo(
          () => ({
            opacity: 1,
            transform: 'translateY(0)',
          }),
          [],
        )}
        initial={useMemo(
          () => ({
            opacity: 0,
            transform: 'translateY(20px)',
          }),
          [],
        )}
        onAnimationComplete={useCallback(() => {
          postIframeResizeEvent();

          if (!didProactiveBubblesShownTrackingEventGetSent) {
            dispatch(
              sendTrackingEvent({
                event_type: 'widget-customer-proactive-bubbles-shown',
              }),
            );
          }
        }, [didProactiveBubblesShownTrackingEventGetSent, dispatch])}
        transition={useMemo(() => ({ delay: 0.5 }), [])}
      >
        <CloseButton
          aria-label='Hide floating buttons'
          isFloatingBar={launchStyle === 'floating_bar'}
          onClick={() => {
            dispatch(setDidUserCloseProactiveChat(true));
            dispatch(
              sendTrackingEvent({
                event_type: 'widget-customer-closed-proactive-bubbles',
              }),
            );

            // `setTimeout()` is necessary, because we need to wait for
            // `<ProactiveBubbles>` to unmount:
            setTimeout(() => {
              postIframeResizeEvent();
            }, 0);
          }}
          outlineColor={legibleButtonOutlineColor}
        ></CloseButton>
      </motion.div>
    </Tooltip>
  );
};

const ProactiveBubbles: React.FC<
  React.PropsWithChildren<ProactiveBubblesProps>
> = ({ className, useResizeIds = true }) => {
  const dispatch = useAppDispatch();
  const workflowsConfig = useAppSelector(selectWorkflowsConfig);
  const isMobile = useAppSelector(selectIsMobile);
  const error = useAppSelector(selectError);
  const greetingMessage = useAppSelector(selectProactivePromptGreeting);
  const isPositionLeft = useAppSelector(selectIsPositionLeft);
  const initialIntentId = useAppSelector(selectInitialIntentId);
  const initialQuery = useAppSelector(selectInitialQuery);
  const launchStyle = useAppSelector(selectLaunchStyle);
  const legibleButtonOutlineColor = useAppSelector(
    selectLegibleButtonOutlineColor,
  );
  const placement = useAppSelector(selectPlacement);
  const handlePromptIntentOptionClicked = useHandlePromptIntentOptionClicked();
  const handleWorkflowTriggerOnClick = useHandleWorkflowTriggerOnClick();

  const areIntentsVisible = Boolean(
    workflowsConfig?.display_proactive_chat_intents,
  );
  const isFloatingBar = launchStyle === 'floating_bar';
  const isGreetingVisible = !isFloatingBar;

  const greetingMessageContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const intents = workflowsConfig?.top_n_intents || [];

  const animationTransform = useMemo(() => {
    const shouldAnimateTopToBottom =
      launchStyle === 'floating_bar' &&
      (placement === 'middle' || placement === 'top');
    return shouldAnimateTopToBottom ? 'translateY(-20px)' : 'translateY(20px)';
  }, [launchStyle, placement]);

  const handleGreetingMessageClick = () => {
    if (initialIntentId || initialQuery) {
      handleWorkflowTriggerOnClick();
    } else {
      dispatch(toggleModalVisibility(true));
    }

    dispatch(
      sendTrackingEvent({
        event_type: 'widget-opened',
        mechanism: 'manual-click-on-proactive-greeting',
      }),
    );
  };

  const intentsContainerAnimate = useMemo(
    () => ({
      opacity: 1,
      transform: 'translateY(0)',
    }),
    [],
  );

  const intentsContainerInitial = useMemo(
    () => ({
      opacity: 0,
      transform: animationTransform,
    }),
    [animationTransform],
  );

  const intentsContainerTransition = useMemo(() => ({ delay: 0.5 }), []);

  const greetingMessageAnimate = useMemo(
    () => ({
      opacity: 1,
      transform: 'translateY(0)',
    }),
    [],
  );

  const greetingMessageInitial = useMemo(
    () => ({
      opacity: 0,
      transform: animationTransform,
    }),
    [animationTransform],
  );

  const greetingMessageTransition = useMemo(() => ({ delay: 0.5 }), []);

  return (
    <>
      <Container
        className={className}
        id={useResizeIds ? FORETHOUGHT_PROACTIVE_CHAT_CONTAINER_ID : undefined}
        isFloatingBar={isFloatingBar}
        isPositionLeft={isPositionLeft}
        isVisible={Boolean(greetingMessage || areIntentsVisible) && !error}
        ref={containerRef}
      >
        <MessagesContainer
          isFloatingBar={isFloatingBar}
          isPositionLeft={isPositionLeft}
        >
          {isGreetingVisible && (
            <GreetingMessageWrapper id='woooo' isPositionLeft={isPositionLeft}>
              <CloseProactiveBubbles />
              <motion.div
                animate={greetingMessageAnimate}
                initial={greetingMessageInitial}
                transition={greetingMessageTransition}
              >
                <GreetingMessage
                  aria-describedby='forethought-widget-description'
                  // eslint-disable-next-line react/forbid-component-props
                  dangerouslySetInnerHTML={{
                    __html: safeLinkifyHtml(sanitizeHtml(greetingMessage)),
                  }}
                  id={
                    useResizeIds
                      ? FORETHOUGHT_PROACTIVE_CHAT_GREETING_MESSAGE_ID
                      : undefined
                  }
                  isPositionLeft={isPositionLeft}
                  onClick={handleGreetingMessageClick}
                  onKeyPress={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      handleGreetingMessageClick();
                    }
                  }}
                  outlineColor={legibleButtonOutlineColor}
                  ref={greetingMessageContainerRef}
                  role='button'
                  tabIndex={0}
                />
              </motion.div>
            </GreetingMessageWrapper>
          )}
          {areIntentsVisible && (
            <motion.div
              animate={intentsContainerAnimate}
              initial={intentsContainerInitial}
              onAnimationComplete={() => {
                if (!isGreetingVisible) {
                  postIframeResizeEvent();
                }
              }}
              transition={intentsContainerTransition}
            >
              <IntentsContainer
                id={
                  useResizeIds
                    ? FORETHOUGHT_PROACTIVE_CHAT_INTENTS_CONTAINER_ID
                    : undefined
                }
                isFloatingBar={isFloatingBar}
                isPositionLeft={isPositionLeft}
              >
                {intents.map(
                  (
                    intent: { intent_id: string; intent_name: string },
                    index: number,
                  ) => (
                    <OptionButton
                      aria-label={intent.intent_name}
                      isMobile={isMobile}
                      isProactiveIntent
                      isSelected={false}
                      key={intent.intent_id}
                      onClick={() => {
                        handlePromptIntentOptionClicked(index);

                        dispatch(
                          sendTrackingEvent({
                            event_type: 'widget-opened',
                            mechanism:
                              'manual-click-on-proactive-intent-button',
                          }),
                        );
                      }}
                      text={intent.intent_name}
                    />
                  ),
                )}
              </IntentsContainer>
            </motion.div>
          )}
        </MessagesContainer>
      </Container>
    </>
  );
};

const CloseButton = styled('button')<{
  isFloatingBar: boolean;
  outlineColor: string;
}>`
  box-sizing: border-box;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.theme.palette.colors.white};
  box-shadow: 0px 2px 10px 0px #00000040;
  cursor: pointer;
  border: 2px solid transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: ${props => (props.isFloatingBar ? '4px' : '16px')};
  background-image: url(${CloseIconDark});
  background-repeat: no-repeat;
  background-position: center;

  &:hover,
  &:focus {
    /* inner indicator */
    outline: 2px ${props => props.outlineColor} solid;
    /* outer indicator */
    box-shadow:
      0 0 0 4px ${props => props.theme.palette.colors.white},
      0px 8px 24px rgba(0, 0, 0, 0.1); /* <-- default shadow */
  }
`;

const Container = styled('div')<{
  isFloatingBar: boolean;
  isPositionLeft: boolean;
  isVisible: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: ${props =>
    props.isPositionLeft || props.isFloatingBar ? 'flex-start' : 'flex-end'};
  visibility: ${props => (props.isVisible ? 'visible' : 'hidden')};

  // This has to be width, not max-width.
  // Otherwise, children will be too narrow, and the text will wrap too early.
  width: ${props =>
    props.isFloatingBar ? `calc(100% - ${DEFAULT_MARGIN}px)` : '280px'};

  box-sizing: border-box;

  &.fade-exit {
    opacity: 1;
  }

  &.fade-exit-active {
    opacity: 0;
    transition: all 300ms;
  }
`;

const MessagesContainer = styled('div')<{
  isFloatingBar: boolean;
  isPositionLeft: boolean;
}>`
  margin-bottom: ${props => (props.isFloatingBar ? '4px' : '16px')};
  display: flex;
  flex-direction: column;
  align-items: ${props => (props.isPositionLeft ? 'flex-start' : 'flex-end')};
`;

const GreetingMessageWrapper = styled('div')<{
  isPositionLeft: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: ${props => (props.isPositionLeft ? 'flex-start' : 'flex-end')};

  &.fade-enter {
    opacity: 0;
  }

  &.fade-enter-active {
    opacity: 1;
    transition: all 300ms;
  }
`;

const GreetingMessage = styled('div')<{
  isPositionLeft: boolean;
  outlineColor: string;
}>`
  background-color: ${props => props.theme.palette.colors.white};
  border-radius: ${props =>
    props.isPositionLeft ? '25px 25px 25px 8px' : '25px 25px 8px 25px'};
  padding: ${MESSAGE_PADDING};
  box-shadow: 0px 2px 10px 0px #00000040;
  cursor: pointer;

  // This has to be max-width, not width.
  // Otherwise, the iframe will be too wide.
  max-width: 280px;

  box-sizing: border-box;
  line-height: 22px;

  &:hover,
  &:focus {
    /* inner indicator */
    outline: 2px ${props => props.outlineColor} solid;
    /* outer indicator */
    box-shadow:
      0 0 0 4px ${props => props.theme.palette.colors.white},
      0px 8px 24px rgba(0, 0, 0, 0.1); /* <-- default shadow */
  }

  &:not(:last-child) {
    margin-bottom: 16px;
  }

  p:first-child {
    margin-top: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  & > p:empty {
    height: 20px;
  }
`;

const IntentsContainer = styled('div')<{
  isFloatingBar: boolean;
  isPositionLeft: boolean;
}>`
  display: flex;
  flex-direction: ${props => (props.isFloatingBar ? 'row' : 'column')};
  align-items: ${props =>
    props.isPositionLeft || props.isFloatingBar ? 'flex-start' : 'flex-end'};
  flex-wrap: ${props => (props.isFloatingBar ? 'wrap' : 'nowrap')};
  gap: ${props => (props.isFloatingBar ? '8px' : '0')};

  :not(:empty) {
    margin-top: 16px;
  }

  &.fade-enter {
    opacity: 0;
    max-height: 0;
  }

  &.fade-enter-active {
    opacity: 1;
    max-height: 1000px;
    transition:
      max-height 1000ms,
      opacity 500ms;
  }
`;

export default ProactiveBubbles;
